/* Import External Modules */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

/* Import Leverege Modules*/
import { GlobalState, Select } from '@leverege/ui-redux';
import { DateTimePicker, DateTimePickerV2, Dialog, Dialogs, PropertyGrid, Selector, Toast } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import classnames from 'classnames';

/* Import Local Modules */
import Icons from "../../Icons.js";
import S from './RenewTemporaryUserDialog.css';
import RenewTemporaryUserModel from "../models/RenewTemporaryUser.js";
import Defaults from "./Defaults.js";
import ShareTemporaryUserAccess from "./ShareTemporaryUserAccess.js";
const {
  t
} = I18N.ns('temporaryUserManagement.action.Renew.Dialog');

/**
 * Renews a given temporary user
 */
async function onRenewTemporaryUser({
  data
}) {
  // Get parameters
  const {
    user,
    actions,
    onClose,
    reqData
  } = data;
  const {
    type,
    ...rest
  } = reqData;
  // Try to remove users
  try {
    const temporaryUser = await GlobalState.dispatch(actions.temporaryUser(user).renew(rest));
    Dialogs.show({
      id: 'temporaryUserManagement.action.PostCreateShare',
      component: ShareTemporaryUserAccess,
      props: {
        ...data,
        temporaryUser
      }
    });
    await GlobalState.dispatch(Select.clear('resourceTemporaryUsersTargeted'));
    // Successfully removed users
    Toast.success(t('toastSuccess', {
      user
    }));
    return onClose();
  } catch (error) {
    console.log(error);
    Toast.error(t('toastError'), error.message);
    return onClose();
  }
}

/**
 * Remove users dialog.
 */
export default function RenewTemporaryUserDialog(props) {
  // Get parameters
  const {
    onClose,
    show,
    user,
    timezone: pTimezone,
    timeFormat: pTimeFormat,
    defaultExpirationInterval,
    expirationIntervals: pExpirationIntervals
  } = props;
  const userString = t('userString', {
    user
  });
  const [reqData, setReqData] = useState(RenewTemporaryUserModel.create());
  const timezone = useMemo(() => pTimezone ?? Defaults.timezone, [pTimezone]);
  const timeFormat = useMemo(() => pTimeFormat ?? Defaults.timeFormat, [pTimeFormat]);
  const expirationIntervals = useMemo(() => pExpirationIntervals ?? Defaults.expirationIntervals, [pExpirationIntervals]);
  const [expirationInterval, setExpirationInterval] = useState(defaultExpirationInterval ?? Defaults.defaultExpirationInterval);
  const wSetExpirationInterval = useCallback(({
    value
  }) => {
    const {
      expiresAt,
      validAfter
    } = expirationIntervals.find(ei => ei.name === value).value({
      ...reqData
    });
    const eSet = RenewTemporaryUserModel.setExpiresAt(reqData, expiresAt);
    const toSend = RenewTemporaryUserModel.setValidAfter(eSet, validAfter);
    setExpirationInterval(value);
    setReqData(toSend);
  }, [setExpirationInterval, expirationIntervals, reqData]);
  const wSetReqData = useCallback(({
    value,
    data
  }) => {
    setReqData(RenewTemporaryUserModel[data](reqData, value));
  }, [setReqData, reqData]);
  useEffect(() => wSetExpirationInterval({
    value: expirationInterval
  }), []);

  // Render component
  return /*#__PURE__*/React.createElement(Dialog.Question, {
    dialogTitleVariant: "tempUserEditorTitle|questionTitle|dialogTitle",
    show: show,
    eventData: {
      ...props,
      reqData
    },
    title: t('title'),
    icon: Icons.renewTemporaryUserDialogIcon,
    okay: t('okay'),
    okayVariant: "primary",
    onCancel: onClose,
    onOkay: onRenewTemporaryUser
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    className: classnames(S.container, S.form, S.body),
    variant: "tempUserEditor|tempUser|userEditor|editor",
    vertical: true
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    variant: "bodySmall",
    title: t('message', {
      userString
    })
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('expiresAtLabel')
  }, /*#__PURE__*/React.createElement(Selector, {
    values: expirationIntervals,
    value: expirationInterval,
    valueKey: "name",
    nameKey: "displayName",
    onChange: wSetExpirationInterval
  })), expirationInterval === 'custom' ? [/*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('startDate')
  }, /*#__PURE__*/React.createElement(DateTimePickerV2, {
    timezone: timezone,
    value: new Date(RenewTemporaryUserModel.getValidAfter(reqData)),
    onChange: wSetReqData,
    eventData: "setValidAfter"
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('endDate')
  }, /*#__PURE__*/React.createElement(DateTimePickerV2, {
    timezone: timezone,
    value: new Date(RenewTemporaryUserModel.getExpiresAt(reqData)),
    onChange: wSetReqData,
    eventData: "setExpiresAt"
  }))] : null));
}